import { ReactElement, useMemo, useState } from "react";
import type { GetStaticPaths, GetStaticProps } from "next";
import Link from "next/link";
import { NextSeo } from "next-seo";
import {
  ChevronRightIcon,
  HomeIcon,
  LinkIcon,
  PrinterIcon,
  ShareIcon,
} from "@heroicons/react/24/outline";
import {
  Activity,
  AirVent,
  BatteryCharging,
  BellRing,
  Building2,
  CircuitBoard,
  ClipboardCheck,
  CloudLightning,
  DoorOpen,
  Flame,
  Gauge,
  Info,
  PlugZap,
  ShieldCheck,
  Snowflake,
  Sun,
  Thermometer,
  ThermometerSun,
  Wind,
} from "lucide-react";
import { toast } from "react-toastify";
import { CityGrid } from "~/components/CityGrid";
import SolutionSelector from "~/components/SolutionSelector";
import { MainLayout } from "~/components/layouts/MainLayout";
import { MarginLayout } from "~/components/layouts/MarginLayout";
import { Banner } from "~/components/Banner";
import { FirmCard } from "~/components/FirmCard";
import { CITIES, getCityBySlug, type City } from "~/lib/cities";
import { getCityPageData } from "~/lib/cityData";
import { CITY_SERVICES, type Service } from "~/lib/services";
import { type Firm } from "~/lib/firms";
import {
  getAvailableCityServices,
  getFeaturedCitiesForService,
  getFirmsForCityAndService,
} from "~/lib/firms.server";
import { getCityProfile, type CityProfile } from "~/lib/cityProfiles";
import { getServiceFaqsForCity } from "~/lib/cityServiceFaqs";
import type { ServiceFaq } from "~/lib/serviceFaqs";
import FaqComponent from "~/components/Faq/Faq";
import { type FaqContent } from "~/components/Faq/FaqContent";
import { SafetyFact } from "~/components/SafetyFact";
import { getFactForPage } from "~/lib/facts";
import { NextPageWithLayout } from "~/pages/_app";

const ELECTRICAL_ICONS = ["plug-zap", "gauge", "cloud-lightning", "sun", "battery-charging", "circuit-board"] as const;
const GAS_ICONS = ["flame", "activity", "thermometer", "clipboard-check"] as const;

const ICON_BY_SERVICE = {
  "plug-zap": PlugZap,
  "gauge": Gauge,
  "cloud-lightning": CloudLightning,
  "sun": Sun,
  "battery-charging": BatteryCharging,
  "circuit-board": CircuitBoard,
  "flame": Flame,
  "activity": Activity,
  "thermometer": Thermometer,
  "clipboard-check": ClipboardCheck,
  "wind": Wind,
  "air-vent": AirVent,
  "snowflake": Snowflake,
  "thermometer-sun": ThermometerSun,
  "bell-ring": BellRing,
  "shield-check": ShieldCheck,
  "door-open": DoorOpen,
  "building-2": Building2,
};

type SortBy = "rating" | "reviews" | "hours24" | "name";

const SORT_OPTIONS: { value: SortBy; label: string }[] = [
  { value: "rating",  label: "Najlepsza ocena" },
  { value: "reviews", label: "Najwięcej opinii" },
  { value: "hours24", label: "Czynne 24/7" },
  { value: "name",    label: "Nazwa A–Z" },
];

const plCollator = new Intl.Collator("pl");

type Props = {
  city: City;
  service: Service;
  firms: Firm[];
  faqs: ServiceFaq[];
  cityProfile: CityProfile | null;
  fact: string;
  availableServices: Service[];
  relatedCities: City[];
};

const normalizeSearchValue = (value: string): string =>
  ` ${value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()} `;

const getCityAliases = (city: City): string[] => {
  const locativeWithoutPreposition = city.nameLocative.replace(/^w[e]?\s+/i, "");

  return [city.name, locativeWithoutPreposition, city.slug.replace(/-/g, " ")]
    .map((alias) => normalizeSearchValue(alias).trim())
    .filter((alias, index, aliases) => alias.length > 2 && aliases.indexOf(alias) === index);
};

const CityServicePage: NextPageWithLayout<Props> = ({
  city,
  service,
  firms,
  faqs,
  cityProfile,
  fact,
  availableServices,
  relatedCities,
}) => {
  const pageTitle = `${service.name} ${city.nameLocative}`;
  const baseUrl =
    (process.env.NEXT_PUBLIC_BASE_URL ?? "https://inspektomat.pl").replace(
      /\/+$/,
      ""
    );
  const pageUrl = `${baseUrl}/${service.slug}/${city.slug}/`;
  const servicePageUrl = `${baseUrl}/${service.slug}/`;
  const seoServiceLabel = service.name.length > 48 ? service.shortName : service.name;
  const seoTitle = `${seoServiceLabel} ${city.nameLocative} - firmy i ceny`;
  const shareText = `Sprawdź listę firm wykonujących ${service.nameGenitive} ${city.nameLocative}.`;

  const servicePrice = cityProfile
    ? (ELECTRICAL_ICONS as readonly string[]).includes(service.icon)
      ? cityProfile.electricalPrice
      : (GAS_ICONS as readonly string[]).includes(service.icon)
      ? cityProfile.gasPrice
      : null
    : null;
  const seoDescription = servicePrice
    ? `Porównaj firmy wykonujące ${service.nameGenitive} ${city.nameLocative}. Sprawdź opinie i orientacyjne ceny ${servicePrice} zł netto oraz wybierz wykonawcę w swojej okolicy.`
    : `Porównaj firmy wykonujące ${service.nameGenitive} ${city.nameLocative}. Sprawdź opinie, lokalne oferty i wybierz wykonawcę w swojej okolicy.`;
  const cityData = getCityPageData(city.slug);
  const bboxEncoded = cityData.mapBbox.replace(/,/g, "%2C");
  const markerEncoded = cityData.mapMarker.replace(/,/g, "%2C");
  const mapSrc = `https://www.openstreetmap.org/export/embed.html?bbox=${bboxEncoded}&layer=mapnik&marker=${markerEncoded}`;
  const [sortBy, setSortBy] = useState<SortBy>("rating");

  const sortedFirms = useMemo(() => {
    if (sortBy === "rating") return firms;
    return [...firms].sort((a, b) => {
      switch (sortBy) {
        case "reviews": {
          const diff = (b.ratingCount ?? 0) - (a.ratingCount ?? 0);
          return diff !== 0 ? diff : (b.rating ?? 0) - (a.rating ?? 0);
        }
        case "hours24": {
          const diff = Number(b.hours24) - Number(a.hours24);
          return diff !== 0 ? diff : (b.rating ?? 0) - (a.rating ?? 0);
        }
        case "name":
          return plCollator.compare(a.name, b.name);
        default:
          return 0;
      }
    });
  }, [firms, sortBy]);

  const cityAliases = getCityAliases(city);
  const [localFirms, regionalFirms] = sortedFirms.reduce<[Firm[], Firm[]]>(
    (groups, firm) => {
      const haystack = normalizeSearchValue(
        [firm.name, firm.address, firm.priceNote, ...firm.services].join(" ")
      );
      const isLocal = cityAliases.some((alias) => haystack.includes(` ${alias} `));

      groups[isLocal ? 0 : 1].push(firm);
      return groups;
    },
    [[], []]
  );
  const printFirms = [...localFirms, ...regionalFirms];
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "@id": `${pageUrl}#service`,
        name: pageTitle,
        serviceType: service.shortName,
        description: `${service.longDescription ?? service.description} Dostępne oferty ${city.nameLocative}.`,
        url: pageUrl,
        category: service.category,
        provider: {
          "@id": `${baseUrl}/#organization`,
        },
        areaServed: {
          "@type": "City",
          name: city.name,
        },
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": pageUrl,
        },
        additionalProperty: [
          service.frequency
            ? {
                "@type": "PropertyValue",
                name: "Częstotliwość kontroli",
                value: service.frequency,
              }
            : null,
          service.legalBasis
            ? {
                "@type": "PropertyValue",
                name: "Podstawa prawna",
                value: service.legalBasis,
              }
            : null,
          servicePrice
            ? {
                "@type": "PropertyValue",
                name: `Orientacyjna cena ${city.nameLocative}`,
                value: `${servicePrice} zł netto`,
              }
            : null,
          {
            "@type": "PropertyValue",
            name: "Liczba dostępnych firm",
            value: String(firms.length),
          },
        ].filter(Boolean),
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumbs`,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Strona główna",
            item: `${baseUrl}/`,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: service.name,
            item: servicePageUrl,
          },
          {
            "@type": "ListItem",
            position: 3,
            name: city.name,
            item: pageUrl,
          },
        ],
      },
      ...(faqs.length > 0
        ? [
            {
              "@type": "FAQPage",
              "@id": `${pageUrl}#faq`,
              mainEntity: faqs.map(({ q, a }) => ({
                "@type": "Question",
                name: q,
                acceptedAnswer: { "@type": "Answer", text: a },
              })),
            },
          ]
        : []),
    ],
  };

  const copyPageLink = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(pageUrl);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = pageUrl;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "absolute";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }

      toast.success("Link skopiowany.");
    } catch {
      toast.error("Nie udało się skopiować linku.");
    }
  };

  const handleShare = async () => {
    if (!navigator.share) {
      await copyPageLink();
      return;
    }

    try {
      await navigator.share({
        title: pageTitle,
        text: shareText,
        url: pageUrl,
      });
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        return;
      }

      await copyPageLink();
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(structuredData),
        }}
      />

      <NextSeo
        title={seoTitle}
        description={seoDescription}
        canonical={pageUrl}
        noindex={false}
        openGraph={{
          title: seoTitle,
          description: seoDescription,
          url: pageUrl,
        }}
      />

      <div className="print-hidden">
        <Banner title={pageTitle} />
      </div>

      {/* Breadcrumbs */}
      <MarginLayout className="mx-auto max-w-7xl !mt-0 !py-3 print-hidden">
        <nav className="flex items-center gap-1.5 py-2 text-sm text-gray-500">
          <Link href="/" className="flex items-center gap-1 hover:text-sky-700">
            <HomeIcon className="h-4 w-4" />
            Strona główna
          </Link>
          <ChevronRightIcon className="h-3.5 w-3.5 text-gray-300" />
          <Link href={`/${service.slug}/`} className="hover:text-sky-700">
            {service.name}
          </Link>
          <ChevronRightIcon className="h-3.5 w-3.5 text-gray-300" />
          <span className="text-gray-800">{city.name}</span>
        </nav>
      </MarginLayout>

      {/* Firm listings */}
      <MarginLayout className="mx-auto max-w-7xl !mt-0 !pb-4 !pt-2">
        <h2 className="print-results-title mb-5 mt-1 text-2xl font-bold text-gray-900 sm:text-3xl">
          Firmy wykonujące {service.nameGenitive} {city.nameLocative}
        </h2>
        <div className="print-hidden mb-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2">
            {SORT_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setSortBy(opt.value)}
                className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                  sortBy === opt.value
                    ? "border-sky-700 bg-sky-700 text-white"
                    : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 lg:justify-end">
            <button
              type="button"
              onClick={handleShare}
              className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-4 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:border-sky-300 hover:bg-sky-50 hover:text-sky-700"
            >
              <ShareIcon className="h-4 w-4" />
              Udostępnij
            </button>
            <button
              type="button"
              onClick={copyPageLink}
              className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-4 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:border-sky-300 hover:bg-sky-50 hover:text-sky-700"
            >
              <LinkIcon className="h-4 w-4" />
              Skopiuj link
            </button>
            <button
              type="button"
              onClick={handlePrint}
              className="inline-flex items-center gap-2 rounded-full bg-sky-700 px-4 py-1.5 text-sm font-medium text-white transition-colors hover:bg-sky-800"
            >
              <PrinterIcon className="h-4 w-4" />
              Drukuj / Zapisz jako PDF
            </button>
          </div>
        </div>
        <>
          <div className="print-only">
            <ol className="print-document-list space-y-2">
              {printFirms.map((firm) => (
                <li
                  key={`print-${firm.id}`}
                  className="print-document-line border-b border-gray-200 pb-2 text-sm text-gray-800 last:border-b-0"
                >
                  <span className="font-semibold text-gray-900">{firm.name}</span>
                  {firm.hours24 ? (
                    <>
                      {", "}
                      <span>Czynne 24/7</span>
                    </>
                  ) : null}
                  {", "}
                  <span>{firm.address}</span>
                  {", "}
                  <span>
                    {firm.priceRange || "Brak danych o cenie"}
                    {firm.priceNote ? ` (${firm.priceNote})` : ""}
                  </span>
                  {", "}
                  <span>Telefon: {firm.phone ?? "Brak numeru telefonu"}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="print-hidden space-y-8">
            {localFirms.length > 0 ? (
              <section>
                <div className="print-firm-grid grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {localFirms.map((firm) => (
                    <FirmCard key={firm.id} firm={firm} />
                  ))}
                </div>
              </section>
            ) : null}

            {regionalFirms.length > 0 ? (
              <section>
                <div className="mb-4 flex items-end justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Firmy obsługujące również {city.nameLocative}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Firmy regionalne i z sąsiednich miast, które pokazujemy jako dodatkowe opcje.
                    </p>
                  </div>
                  <span className="text-sm font-medium text-sky-700">
                    {regionalFirms.length}
                  </span>
                </div>
                <div className="print-firm-grid grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {regionalFirms.map((firm) => (
                    <FirmCard key={`${firm.id}-regional`} firm={firm} />
                  ))}
                </div>
              </section>
            ) : null}
          </div>
        </>
      </MarginLayout>

      {/* Safety fact */}
      <MarginLayout className="mx-auto max-w-7xl print-hidden">
        <SafetyFact fact={fact} />
      </MarginLayout>

      {/* Intro */}
      <MarginLayout className="mx-auto max-w-7xl print-hidden">
        <div className="grid gap-8 lg:grid-cols-2 lg:items-start">
          {/* Map + city stats */}
          <div className="space-y-3">
            <div className="overflow-hidden rounded-xl shadow-md">
              <iframe
                title={`Mapa ${city.name} — ${service.name}`}
                src={mapSrc}
                width="100%"
                height="360"
                loading="lazy"
                className="border-0"
                aria-label={`Mapa ${city.name}`}
              />
              <div className="bg-gray-50 px-4 py-2 text-xs text-gray-400">
                {cityData.mapCaption}
              </div>
            </div>
            {cityData.stats.length > 0 && (
              <div className="grid grid-cols-2 gap-2">
                {cityData.stats.map((stat, i) => (
                  <div key={i} className="rounded-lg border border-gray-100 bg-white p-3 shadow-sm">
                    <p className="text-base font-bold text-sky-700">{stat.value}</p>
                    <p className="mt-0.5 text-xs text-gray-500">{stat.label}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Description + stats */}
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-gray-900">
              {service.name} {city.nameLocative} — znajdź sprawdzoną firmę
            </h2>
            <p className="text-gray-600 leading-relaxed">
              Nasza platforma łączy właścicieli nieruchomości {city.nameLocative}{" "}
              z certyfikowanymi firmami wykonującymi {service.nameGenitive}.
              Porównaj oferty, sprawdź opinie i wybierz specjalistę
              dopasowanego do Twojego budynku.
            </p>
            {service.longDescription && (
              <p className="text-gray-600 leading-relaxed">{service.longDescription}</p>
            )}

            {/* Quick stats */}
            <div className="flex flex-col gap-3 rounded-lg bg-sky-50 p-4">
              {service.frequency && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-sky-700">
                    Częstotliwość
                  </p>
                  <p className="mt-0.5 text-sm font-medium text-gray-800">
                    {service.frequency}
                  </p>
                </div>
              )}
              {service.legalBasis && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-sky-700">
                    Podstawa prawna
                  </p>
                  <p className="mt-0.5 text-sm font-medium text-gray-800">
                    {service.legalBasis}
                  </p>
                </div>
              )}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-sky-700">
                  Firmy {city.nameLocative}
                </p>
                <p className="mt-0.5 text-sm font-medium text-gray-800">
                  {firms.length} dostępnych w bazie
                </p>
              </div>
              {servicePrice && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-sky-700">
                    Orientacyjna cena
                  </p>
                  <p className="mt-0.5 text-sm font-medium text-gray-800">
                    {servicePrice} zł netto
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </MarginLayout>

      {/* Climate callout */}
      {cityProfile?.climateNote && (
        <MarginLayout className="mx-auto max-w-7xl print-hidden">
          <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
            <p>
              <span className="font-semibold">{city.name}:</span>{" "}
              {cityProfile.climateNote}, co dodatkowo uzasadnia regularne przeglądy.
            </p>
          </div>
        </MarginLayout>
      )}

      {/* City context: areas, nearby */}
      <MarginLayout className="mx-auto max-w-7xl space-y-6 print-hidden">
        {cityData.areas.length > 0 && (
          <div>
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
              Obsługiwane dzielnice
            </h3>
            <div className="flex flex-wrap gap-2">
              {cityData.areas.map((area) => (
                <span
                  key={area}
                  className="rounded-full border border-sky-100 bg-sky-50 px-3 py-1 text-xs text-sky-700"
                >
                  {area}
                </span>
              ))}
            </div>
          </div>
        )}

        {cityData.nearbyMunicipalities.length > 0 && (
          <div>
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
              Obsługujemy również okolice
            </h3>
            <div className="flex flex-wrap gap-2">
              {cityData.nearbyMunicipalities.map((m) => (
                <span
                  key={m}
                  className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs text-gray-600"
                >
                  {m}
                </span>
              ))}
            </div>
          </div>
        )}
      </MarginLayout>

      {/* FAQ */}
      {faqs.length > 0 && (
        <div className="print-hidden">
          <FaqComponent
            sections={[
              {
                title: `Najczęstsze pytania — ${service.name} ${city.nameLocative}`,
                items: faqs.map((faq, i) => ({ id: i, question: faq.q, answer: faq.a })),
              } satisfies FaqContent,
            ]}
            selectedIds={faqs.map((_, i) => i)}
          />
        </div>
      )}

      {/* Other services in this city */}
      {availableServices.length > 0 && (
        <section className="bg-gray-50 pt-6 pb-8 sm:pt-8 sm:pb-10 print-hidden">
          <MarginLayout>
            <h2 className="mb-4 text-xl font-bold text-gray-900">
              Inne przeglądy {city.nameLocative}
            </h2>
            <SolutionSelector
              items={availableServices.map((availableService) => ({
                icon: ICON_BY_SERVICE[availableService.icon],
                label: availableService.shortName,
                href: `/${availableService.slug}/${city.slug}/`,
              }))}
              gridClassName="grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4"
            />
          </MarginLayout>
        </section>
      )}

      {relatedCities.length > 0 && (
        <div className="print-hidden">
          <CityGrid
            serviceSlug={service.slug}
            cities={relatedCities}
            title={`Sprawdź także ${service.name.toLowerCase()} w innych miastach`}
            subtitle="Wybrane miasta z tej samej usługi, które warto porównać z Twoją lokalizacją."
            showAllByDefault
          />
        </div>
      )}
    </>
  );
};

CityServicePage.getLayout = (page: ReactElement) => (
  <MainLayout>{page}</MainLayout>
);

export default CityServicePage;

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = CITY_SERVICES.flatMap((service) =>
    CITIES.filter(
      (city) => getFirmsForCityAndService(city.slug, service.slug).length > 0
    ).map((city) => ({
      params: { slug: service.slug, city: city.slug },
    }))
  );
  return { paths, fallback: false };
};

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  const serviceSlug = params?.slug as string;
  const citySlug = params?.city as string;

  const service = CITY_SERVICES.find((s) => s.slug === serviceSlug);
  const city = getCityBySlug(citySlug);

  if (!city || !service) return { notFound: true };

  const firms = getFirmsForCityAndService(citySlug, serviceSlug);
  if (firms.length === 0) return { notFound: true };

  const cityProfile = getCityProfile(citySlug);
  const faqs = cityProfile ? getServiceFaqsForCity(serviceSlug, cityProfile) : [];
  const availableServices = getAvailableCityServices(citySlug).filter(
    (availableService) => availableService.slug !== serviceSlug
  );
  const relatedCities = getFeaturedCitiesForService(serviceSlug, {
    currentCitySlug: citySlug,
    priorities: ["P0", "P1"],
    limit: 8,
  });

  return {
    props: {
      city,
      service,
      firms,
      faqs,
      cityProfile: cityProfile ?? null,
      fact: getFactForPage(`${serviceSlug}/${citySlug}`),
      availableServices,
      relatedCities,
    },
  };
};
